import LaunchIcon from '@mui/icons-material/Launch';
import { Box } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { $api } from 'shared/api/api';
import { SavingsSummaryProductDetailsDrawer } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryProductDetailsDrawer';

import type { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import type { ProductMetricsDto } from 'entities/ReportSavingsSummary';
import type { ExtendedAxiosRequestConfig } from 'shared/api/types';

interface ProductDrillInContextHeaderProps {
    sku: string;
    product: ProductMetricsDto | null;
}

export const ProductDrillInContextHeader = ({
    sku,
    product,
}: ProductDrillInContextHeaderProps) => {
    const { t } = useTranslation();
    const [selectedProductSku, setSelectedProductSku] = useState<string | null>(
        null,
    );
    const [productItem, setProductItem] = useState<CatalogItem | null>(null);

    useEffect(() => {
        let isCancelled = false;

        const fetchProductItem = async () => {
            try {
                const config: ExtendedAxiosRequestConfig = {
                    keysToNotConvert: ['specifications'],
                };
                const response = await $api.get<CatalogItem>(
                    `/v1/catalogs/items/${encodeURIComponent(sku)}`,
                    config,
                );

                if (!isCancelled) {
                    setProductItem(response.data);
                }
            } catch {
                if (!isCancelled) {
                    setProductItem(null);
                }
            }
        };

        fetchProductItem();

        return () => {
            isCancelled = true;
        };
    }, [sku]);

    const openProductDetails = useCallback(() => {
        setSelectedProductSku(sku);
    }, [sku]);

    const closeProductDetails = useCallback(() => {
        setSelectedProductSku(null);
    }, []);

    const productImageUrl =
        productItem?.imageUrl ?? productItem?.thumbnailUrl ?? null;

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    gap: '24px',
                    alignItems: 'flex-start',
                    marginBottom: '30px',
                    '&:hover .context-open-icon': {
                        opacity: 1,
                    },
                }}
            >
                {productImageUrl ? (
                    <Box
                        component="img"
                        src={productImageUrl}
                        alt={String(
                            product?.itemName ??
                                t(
                                    'savingsSummary.productDetailsImageAlt',
                                    'Product image',
                                ),
                        )}
                        sx={{
                            width: '100px',
                            height: '100px',
                            borderRadius: '8px',
                            backgroundColor: '#FFF',
                            objectFit: 'cover',
                            flexShrink: 0,
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            width: '100px',
                            height: '100px',
                            borderRadius: '8px',
                            backgroundColor: '#FFF',
                            border: '1px solid #DFDFDF',
                            flexShrink: 0,
                            boxSizing: 'border-box',
                        }}
                    />
                )}
                <Box
                    sx={{
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            color: 'common.black',
                            fontSize: '13px',
                            lineHeight: '20px',
                            fontWeight: 400,
                        }}
                    >
                        {product?.brand ? <span>{product.brand}</span> : null}
                        <span>{product?.itemSku ?? sku}</span>
                    </Box>
                    <Box
                        onClick={openProductDetails}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '10px',
                            cursor: 'pointer',
                            width: 'fit-content',
                            maxWidth: '100%',
                            '&:hover .product-title-text': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        <Box
                            component="span"
                            className="product-title-text"
                            sx={{
                                fontSize: '24px',
                                lineHeight: '20px',
                                fontWeight: 600,
                                color: 'common.black',
                                minWidth: 0,
                                wordBreak: 'break-word',
                            }}
                        >
                            {product?.itemName ?? sku}
                        </Box>
                        <LaunchIcon
                            className="context-open-icon"
                            sx={{
                                fontSize: '20px',
                                color: 'common.black',
                                flexShrink: 0,
                                opacity: 0,
                                transition: 'opacity 120ms ease-out',
                            }}
                        />
                    </Box>
                </Box>
            </Box>
            <SavingsSummaryProductDetailsDrawer
                sku={selectedProductSku}
                open={Boolean(selectedProductSku)}
                onClose={closeProductDetails}
                initialItem={productItem}
            />
        </>
    );
};
